// Entrada por NF a partir do XML da NF-e: o backend lê cabeçalho, itens e custos (frete rateado
// já incluído no valor de cada linha); aqui o usuário só decide, linha a linha, qual produto do
// estoque o item é (e o fator da embalagem) ou que ele não é estoque. As decisões viram vínculos
// fornecedor+código → produto e voltam sugeridas no próximo XML. Regras em
// backend/services/estoque/importacao_xml.py.
import { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, FileCode2, Link2, Plus, Upload, X } from "lucide-react";
import { postEstoqueLancarXmlNF, postEstoquePreviaXmlNF } from "../../api";
import { ModalProduto } from "./CadastrosEstoqueView";
import { ProdutoSelect, formatData, hojeISO, mensagemErro } from "./comum";
import { AvisoErro, Campo, NumeroInput, btnStyle, formatBRL, formatQtd, inputStyle, numeroParaInput, parseNumero, useIsMobile } from "./ui";

const num = (texto) => {
  const p = parseNumero(texto);
  return p === null ? null : Number(p);
};
const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;
const formatCnpj = (c) => (c && c.length === 14 ? `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}` : c);

function decisaoInicial(linha) {
  const s = linha.sugestao;
  if (s?.ignorar) return { modo: "ignorar", produto_id: null, unidade_nf_id: "", fator: "", origem: s.origem };
  if (s) return { modo: "produto", produto_id: s.produto_id, unidade_nf_id: String(s.unidade_nf_id ?? ""), fator: numeroParaInput(s.fator_conversao), origem: s.origem };
  return { modo: "", produto_id: null, unidade_nf_id: linha.unidade_sugerida_id ? String(linha.unidade_sugerida_id) : "", fator: "", origem: null };
}

const decidida = (d) => d.modo === "ignorar" || (d.modo === "produto" && d.produto_id && d.unidade_nf_id && num(d.fator) > 0);

function Badge({ cor, children }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: `${cor}1F`, color: cor, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

// ─── Linha do XML ─────────────────────────────────────────────────────────────

function LinhaXml({ C, linha, decisao, produtos, unidades, onChange, onNovoProduto }) {
  const produto = produtos.find((p) => p.id === decisao.produto_id);
  const siglaPadrao = produto?.unidade_padrao_sigla;
  const unidadeNF = unidades.find((u) => u.id === Number(decisao.unidade_nf_id));
  const qtd = Number(linha.quantidade);
  const valor = Number(linha.valor_item);
  const fator = num(decisao.fator);
  const convertida = decisao.modo === "produto" && fator ? qtd * fator : null;
  const custoUnXml = valor / qtd;
  const anterior = linha.preco_anterior;
  const variacao = anterior ? custoUnXml / Number(anterior.valor_unitario) - 1 : null;
  const ok = decidida(decisao);
  const acrescimos = Number(linha.valor_frete) + Number(linha.valor_outros) - Number(linha.valor_desconto);

  function escolherProduto(id) {
    const p = produtos.find((x) => x.id === id);
    const novo = { ...decisao, modo: "produto", produto_id: id, origem: null };
    if (p && Number(novo.unidade_nf_id) === p.unidade_padrao_id && !novo.fator) novo.fator = "1";
    onChange(novo);
  }

  const segmento = (ativo) => ({
    flex: 1, minHeight: 38, border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700,
    background: ativo ? C.card : "transparent", color: ativo ? C.text : C.muted, boxShadow: ativo ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
  });

  return (
    <div style={{ background: C.card, border: `1px solid ${ok ? C.border : C.warning}`, borderLeft: `4px solid ${ok ? (decisao.modo === "ignorar" ? C.muted : C.success) : C.warning}`, borderRadius: 12, padding: 12, opacity: decisao.modo === "ignorar" ? 0.75 : 1 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, wordBreak: "break-word" }}>
            <span style={{ color: C.muted, fontWeight: 600 }}>#{linha.n_item} </span>{linha.descricao}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            Cód. {linha.codigo}{linha.ean ? ` · EAN ${linha.ean}` : " · sem EAN"}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
            {decisao.origem === "VINCULO" && <Badge cor={C.success}><Link2 size={11} /> Vínculo salvo</Badge>}
            {decisao.origem === "EAN" && <Badge cor={C.brand}><Link2 size={11} /> Pelo EAN{linha.sugestao?.fornecedor_nome ? ` (${linha.sugestao.fornecedor_nome})` : ""}</Badge>}
            {anterior && (
              <Badge cor={variacao > 0.005 ? C.warning : variacao < -0.005 ? C.success : C.muted}>
                Última compra {formatBRL(anterior.valor_unitario)}/{linha.unidade_xml} · {anterior.fornecedor_nome} · {formatData(anterior.data_emissao)}
              </Badge>
            )}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{formatBRL(valor)}</div>
          <div style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap" }}>{formatQtd(qtd)} {linha.unidade_xml} × {formatBRL(linha.valor_unitario)}</div>
          {Math.abs(acrescimos) >= 0.005 && (
            <div style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap" }}>
              {Number(linha.valor_frete) > 0 && `+ frete ${formatBRL(linha.valor_frete)}`}
              {Number(linha.valor_desconto) > 0 && ` − desc. ${formatBRL(linha.valor_desconto)}`}
              {Number(linha.valor_outros) > 0 && ` + outros ${formatBRL(linha.valor_outros)}`}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, background: C.bgActive, borderRadius: 8, padding: 3, marginBottom: decisao.modo === "produto" ? 10 : 0 }}>
        <button onClick={() => onChange({ ...decisao, modo: "produto", origem: decisao.modo === "produto" ? decisao.origem : null })} style={segmento(decisao.modo === "produto")}>Produto do estoque</button>
        <button onClick={() => onChange({ ...decisao, modo: "ignorar", origem: null })} style={segmento(decisao.modo === "ignorar")}>Não é estoque</button>
      </div>

      {decisao.modo === "produto" && (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Campo label="Produto do estoque" C={C} obrigatorio>
                <ProdutoSelect C={C} produtos={produtos} value={decisao.produto_id} onChange={escolherProduto} />
              </Campo>
            </div>
            <button onClick={onNovoProduto} title="Cadastrar produto novo" aria-label="Cadastrar produto novo" style={{ ...btnStyle(C.brand, true), flexShrink: 0, padding: "10px 12px" }}>
              <Plus size={15} />
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
            <Campo label={`Unidade da NF (${linha.unidade_xml})`} C={C} obrigatorio>
              <select value={decisao.unidade_nf_id} onChange={(e) => onChange({ ...decisao, unidade_nf_id: e.target.value })} style={inputStyle(C)}>
                <option value="">—</option>
                {unidades.map((u) => <option key={u.id} value={u.id}>{u.sigla}</option>)}
              </select>
            </Campo>
            <Campo label={siglaPadrao ? `Fator (${siglaPadrao} por ${unidadeNF?.sigla || "emb."})` : "Fator"} C={C} obrigatorio
              ajuda={siglaPadrao ? `Ex.: pacote com 12 rolos → 12` : undefined}>
              <NumeroInput C={C} casas={6} value={decisao.fator} onChange={(v) => onChange({ ...decisao, fator: v })} />
            </Campo>
          </div>
          {convertida !== null && siglaPadrao && (
            <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: C.text }}>
              = {formatQtd(convertida)} {siglaPadrao} · {formatBRL(valor / convertida)}/{siglaPadrao}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Tela ─────────────────────────────────────────────────────────────────────

export default function ImportacaoXmlNF({ C, contexto, produtos, unidades, categorias, perm, onProdutoCriado, onCancelar, onSalva }) {
  const isMobile = useIsMobile();
  const [arquivo, setArquivo] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [decisoes, setDecisoes] = useState({});
  const [cab, setCab] = useState({ data_entrada: hojeISO(), observacao: "" });
  const [justificativa, setJustificativa] = useState("");
  const [erro, setErro] = useState("");
  const [lendo, setLendo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [novoProdutoPara, setNovoProdutoPara] = useState(null); // n_item
  const [arrastando, setArrastando] = useState(false);

  const produtosAtivos = useMemo(() => produtos.filter((p) => p.ativo), [produtos]);
  const unidadesAtivas = useMemo(() => unidades.filter((u) => u.ativo), [unidades]);
  const tolerancia = Number(contexto.nf_tolerancia_valor);

  async function ler(file) {
    if (!file) return;
    setErro("");
    setPrevia(null);
    setLendo(true);
    try {
      const p = await postEstoquePreviaXmlNF(file);
      setArquivo(file);
      setPrevia(p);
      setDecisoes(Object.fromEntries(p.linhas.map((l) => [l.n_item, decisaoInicial(l)])));
      const hoje = hojeISO();
      setCab((c) => ({ ...c, data_entrada: p.data_emissao > hoje ? hoje : c.data_entrada }));
    } catch (e) {
      setArquivo(null);
      setErro(mensagemErro(e));
    } finally {
      setLendo(false);
    }
  }

  // A decisão de uma linha vale para as outras linhas ainda pendentes com o mesmo código
  // (o mesmo produto repetido em várias linhas da nota).
  function mudarDecisao(linha, nova) {
    setDecisoes((atual) => {
      const prox = { ...atual, [linha.n_item]: nova };
      if (decidida(nova)) {
        for (const outra of previa.linhas) {
          if (outra.n_item !== linha.n_item && outra.codigo === linha.codigo && !decidida(atual[outra.n_item])) {
            prox[outra.n_item] = { ...nova };
          }
        }
      }
      return prox;
    });
  }

  const resumo = useMemo(() => {
    if (!previa) return null;
    const grupos = new Map();
    let ignorados = 0;
    let qtdIgnorados = 0;
    let pendentes = 0;
    for (const l of previa.linhas) {
      const d = decisoes[l.n_item];
      if (!d || !decidida(d)) { pendentes += 1; continue; }
      if (d.modo === "ignorar") { ignorados += Number(l.valor_item); qtdIgnorados += 1; continue; }
      const chave = `${d.produto_id}|${d.unidade_nf_id}|${num(d.fator)}`;
      const g = grupos.get(chave) || { produto: produtosAtivos.find((p) => p.id === d.produto_id), qtd: 0, valor: 0, linhas: 0 };
      g.qtd += Number(l.quantidade) * num(d.fator);
      g.valor += Number(l.valor_item);
      g.linhas += 1;
      grupos.set(chave, g);
    }
    const itens = Array.from(grupos.values());
    const somaItens = itens.reduce((s, g) => s + g.valor, 0);
    const diferenca = round2(Number(previa.valor_total) - somaItens - ignorados);
    return { itens, ignorados: round2(ignorados), qtdIgnorados, pendentes, somaItens: round2(somaItens), diferenca };
  }, [previa, decisoes, produtosAtivos]);

  const divergente = resumo && resumo.pendentes === 0 && Math.abs(resumo.diferenca) > tolerancia + 1e-9;

  async function lancar() {
    if (resumo.pendentes > 0) { setErro(`${resumo.pendentes} item(ns) sem decisão: escolha o produto do estoque ou marque “Não é estoque”.`); return; }
    if (resumo.itens.length === 0) { setErro("Todos os itens estão como “Não é estoque” — não há entrada para lançar."); return; }
    if (!cab.data_entrada) { setErro("Informe a data de entrada."); return; }
    if (divergente && !justificativa.trim()) { setErro("A soma não bate com o total da NF — informe a justificativa."); return; }
    setErro("");
    setSalvando(true);
    try {
      const nf = await postEstoqueLancarXmlNF(arquivo, {
        data_entrada: cab.data_entrada,
        observacao: cab.observacao.trim() || null,
        justificativa_divergencia: divergente ? justificativa.trim() : null,
        decisoes: previa.linhas.map((l) => {
          const d = decisoes[l.n_item];
          return d.modo === "ignorar"
            ? { n_item: l.n_item, ignorar: true }
            : { n_item: l.n_item, produto_id: d.produto_id, unidade_nf_id: Number(d.unidade_nf_id), fator_conversao: parseNumero(d.fator) };
        }),
      });
      onSalva(nf);
    } catch (e) {
      setErro(mensagemErro(e));
      setSalvando(false);
    }
  }

  const info = (rotulo, valor, extra) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ color: C.muted, fontSize: 11 }}>{rotulo}</div>
      <div style={{ fontWeight: 700, wordBreak: "break-word" }}>{valor}</div>
      {extra && <div style={{ color: C.muted, fontSize: 12 }}>{extra}</div>}
    </div>
  );

  return (
    <div style={{ maxWidth: 960 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Entrada pelo XML da NF-e</h2>
        <button onClick={onCancelar} style={btnStyle(C.muted, true)}><X size={15} /> Cancelar</button>
      </div>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>

      {/* Arquivo */}
      <label
        onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
        onDragLeave={() => setArrastando(false)}
        onDrop={(e) => { e.preventDefault(); setArrastando(false); ler(e.dataTransfer.files?.[0]); }}
        style={{
          display: "flex", alignItems: "center", gap: 12, cursor: "pointer", marginBottom: 16,
          background: arrastando ? C.bgActive : C.card, border: `1.5px dashed ${arrastando ? C.brand : C.border}`, borderRadius: 12,
          padding: previa ? "10px 14px" : "28px 16px", justifyContent: previa ? "flex-start" : "center", flexDirection: previa ? "row" : "column", textAlign: "center",
        }}>
        {previa ? <FileCode2 size={18} color={C.brand} /> : <Upload size={26} color={C.brand} />}
        <div style={{ fontSize: 13, color: C.text }}>
          {lendo ? "Lendo o XML..." : previa ? <><strong>{arquivo?.name}</strong> <span style={{ color: C.muted }}>· trocar arquivo</span></> : (
            <>
              <strong>Selecione ou arraste o XML da NF-e</strong>
              <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>O arquivo .xml que o fornecedor envia por e-mail (procNFe). Os itens, valores e o frete vêm dele.</div>
            </>
          )}
        </div>
        <input type="file" accept=".xml,text/xml,application/xml" onChange={(e) => { ler(e.target.files?.[0]); e.target.value = ""; }} style={{ display: "none" }} />
      </label>

      {previa && resumo && (
        <>
          {previa.avisos.map((a) => (
            <div key={a} style={{ display: "flex", gap: 8, alignItems: "flex-start", background: `${C.warning}18`, border: `1px solid ${C.warning}`, borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 12, color: C.text }}>
              <AlertTriangle size={15} color={C.warning} style={{ flexShrink: 0, marginTop: 1 }} /> {a}
            </div>
          ))}

          {/* Cabeçalho lido do XML */}
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, fontSize: 13 }}>
              {info("Fornecedor", previa.fornecedor_nome, <>{formatCnpj(previa.emitente_cnpj)}{previa.fornecedor_novo && <> · <Badge cor={C.brand}>novo — será cadastrado</Badge></>}</>)}
              {info("NF", `${previa.numero}${previa.serie ? `/${previa.serie}` : ""}`, `Emitida em ${formatData(previa.data_emissao)}`)}
              {info("Valor total", formatBRL(previa.valor_total), `${previa.linhas.length} linha(s) no XML`)}
              {info("Destinatário", <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><CheckCircle2 size={14} color={C.success} /> {formatCnpj(previa.destinatario_cnpj)}</span>, previa.destinatario_nome)}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 10, wordBreak: "break-all" }}>Chave {previa.chave_acesso}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginTop: 12 }}>
              <Campo label="Data de entrada" C={C} obrigatorio>
                <input type="date" value={cab.data_entrada} min={[contexto.data_abertura, previa.data_emissao].filter(Boolean).sort().pop()} max={hojeISO()}
                  onChange={(e) => setCab({ ...cab, data_entrada: e.target.value })} style={inputStyle(C)} />
              </Campo>
              <Campo label="Observação" C={C}><input value={cab.observacao} onChange={(e) => setCab({ ...cab, observacao: e.target.value })} style={inputStyle(C)} /></Campo>
            </div>
          </div>

          {/* Linhas */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>Itens da nota</h3>
            <span style={{ fontSize: 12, color: resumo.pendentes ? C.warning : C.success, fontWeight: 700 }}>
              {resumo.pendentes ? `${resumo.pendentes} de ${previa.linhas.length} sem decisão` : "Todos os itens decididos"}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {previa.linhas.map((l) => (
              <LinhaXml key={l.n_item} C={C} linha={l} decisao={decisoes[l.n_item]} produtos={produtosAtivos} unidades={unidadesAtivas}
                onChange={(d) => mudarDecisao(l, d)} onNovoProduto={() => setNovoProdutoPara(l.n_item)} />
            ))}
          </div>

          {/* Pré-lançamento somado por produto */}
          {resumo.itens.length > 0 && (
            <div style={{ marginTop: 16, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
              <h3 style={{ margin: 0, padding: "12px 14px", fontSize: 14, background: C.bgActive }}>Entra no estoque</h3>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ color: C.muted, textTransform: "uppercase", fontSize: 10 }}>
                      {["Produto", "Quantidade", "Valor", "Custo/un."].map((h, i) => (
                        <th key={h} style={{ padding: "8px 14px", textAlign: i === 0 ? "left" : "right", borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {resumo.itens.map((g, i) => (
                      <tr key={i}>
                        <td style={{ padding: "8px 14px", borderBottom: `1px solid ${C.border}` }}>
                          {g.produto?.nome}{g.linhas > 1 && <span style={{ color: C.muted, fontSize: 11 }}> · {g.linhas} linhas somadas</span>}
                        </td>
                        <td style={{ padding: "8px 14px", borderBottom: `1px solid ${C.border}`, textAlign: "right", whiteSpace: "nowrap" }}>{formatQtd(g.qtd)} {g.produto?.unidade_padrao_sigla}</td>
                        <td style={{ padding: "8px 14px", borderBottom: `1px solid ${C.border}`, textAlign: "right" }}>{formatBRL(g.valor)}</td>
                        <td style={{ padding: "8px 14px", borderBottom: `1px solid ${C.border}`, textAlign: "right", whiteSpace: "nowrap" }}>{formatBRL(g.valor / g.qtd)}</td>
                      </tr>
                    ))}
                    {resumo.qtdIgnorados > 0 && (
                      <tr style={{ color: C.muted }}>
                        <td style={{ padding: "8px 14px" }}>Não é estoque ({resumo.qtdIgnorados} {resumo.qtdIgnorados === 1 ? "item" : "itens"})</td>
                        <td />
                        <td style={{ padding: "8px 14px", textAlign: "right" }}>{formatBRL(resumo.ignorados)}</td>
                        <td />
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {divergente && (
            <div style={{ marginTop: 16, background: `${C.danger}10`, border: `1px solid ${C.danger}`, borderRadius: 12, padding: 12 }}>
              <Campo label="Justificativa da divergência" C={C} obrigatorio ajuda="A soma das linhas do XML não fecha com o total da nota — descreva o motivo.">
                <textarea value={justificativa} onChange={(e) => setJustificativa(e.target.value)} rows={2} style={{ ...inputStyle(C), resize: "vertical" }} />
              </Campo>
            </div>
          )}

          {/* Conferência, sempre visível */}
          <div style={{ position: "sticky", bottom: 0, zIndex: 30, marginTop: 16, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: "0 -6px 24px rgba(0,0,0,0.12)", padding: isMobile ? "10px 12px" : "12px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <div style={{ display: "flex", gap: isMobile ? 10 : 20, flexWrap: "wrap", fontSize: 13, flex: 1, justifyContent: isMobile ? "flex-start" : "flex-end" }}>
              <span>Estoque: <strong>{formatBRL(resumo.somaItens)}</strong></span>
              {resumo.ignorados > 0 && <span>Não é estoque: <strong>{formatBRL(resumo.ignorados)}</strong></span>}
              <span>NF: <strong>{formatBRL(previa.valor_total)}</strong></span>
              {resumo.pendentes === 0 && (
                <span style={{ color: divergente ? C.danger : C.success, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {divergente ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />} {divergente ? `Diferença ${formatBRL(resumo.diferenca)}` : "Total confere"}
                </span>
              )}
            </div>
            <button onClick={lancar} disabled={salvando} style={{ ...btnStyle(C.success), opacity: salvando || resumo.pendentes ? 0.6 : 1, minWidth: 150 }}>
              {salvando ? "Lançando..." : "Lançar entrada"}
            </button>
          </div>
        </>
      )}

      {novoProdutoPara !== null && (
        <ModalProduto C={C} categorias={categorias} unidades={unidades} podeEditarLimites={perm.financeiro}
          onClose={() => setNovoProdutoPara(null)}
          onSalvo={(produto) => {
            onProdutoCriado(produto);
            const linha = previa.linhas.find((l) => l.n_item === novoProdutoPara);
            const d = decisoes[novoProdutoPara];
            const nova = { ...d, modo: "produto", produto_id: produto.id, origem: null };
            if (Number(nova.unidade_nf_id) === produto.unidade_padrao_id && !nova.fator) nova.fator = "1";
            mudarDecisao(linha, nova);
            setNovoProdutoPara(null);
          }} />
      )}
    </div>
  );
}
